<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import AuthModal from '@/components/auth/AccountModal.vue'
import WorkspaceMapContainer from '@/components/map/WorkspaceMapContainer.vue'
import MapHeader from '@/components/MapHeader.vue'
import PhotoSlideshow from '@/components/PhotoSlideshow.vue'
import { useEventListener } from '@/composables/useEventListener'
import { apiService } from '@/services/apiService'
import {
  cancelPendingLandingPageImport,
  consumePendingLandingPageImport,
  hasPendingLandingPageImport,
  LANDING_PAGE_IMPORT_REQUEST_EVENT,
  queueLandingPageImport,
} from '@/services/landingPageImportService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { requirePermission } from '@/services/permissionService'
import { rememberPinnedWorkspaceClientId } from '@/services/pinnedService'
import { syncService } from '@/services/syncService'
import { showError, showInfo, showSuccess } from '@/services/toastService'
import { uiStatePersistenceService } from '@/services/uiStatePersistenceService'
import { createWorkspaceRouteLoadCoordinator } from '@/services/workspaceRouteLoadCoordinator'
import { useAuthStore } from '@/stores/authStore'
import { useLibraryStore } from '@/stores/libraryStore'
import { usePhotoStore } from '@/stores/photoStore'
import { useUiStore } from '@/stores/uiStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import { getMixedImportFilePickerAccept, getTrackFilePickerAccept } from '@/utils/filePickerAccept'

// Define props for route parameters
interface Props {
  workspaceId?: string
  photoId?: string
  invitationToken?: string
  tourId?: string
}

const props = defineProps<Props>()
const route = useRoute()
const router = useRouter()
const isOfflineMapAreaSelector = computed(() => route.name === 'offline-map-areas')
const routeLoadCoordinator = createWorkspaceRouteLoadCoordinator(
  () => route.fullPath,
  async (path) => {
    await router.replace(path)
  },
)

// Stores
const workspaceStore = useWorkspaceStore()
const photoStore = usePhotoStore()
const uiStore = useUiStore()
const libraryStore = useLibraryStore()
const authStore = useAuthStore()

// Dialog state
const isInvitationModalOpen = ref(false)
const invitationError = ref<string | undefined>(undefined)
const invitationLoading = ref(false)

// Invitation state
const pendingInvitationToken = ref<string | undefined>(undefined)
const invitationInfo = ref<
  { inviter: string; workspaceName: string; userExists: boolean } | undefined
>(undefined)

const handleInvitationAcceptance = async () => {
  if (!props.invitationToken) {
    showError('No invitation token provided.')
    return
  }

  invitationLoading.value = true
  invitationError.value = undefined

  // First, get invitation info to check if user exists
  const info = await apiService.getInvitationInfo(props.invitationToken)

  if (!info.success) {
    // Set error for form display but don't show toast
    invitationError.value = info.error || 'Failed to get invitation info'
    // Show invitation form even with error so user can see the error message
    pendingInvitationToken.value = props.invitationToken
    isInvitationModalOpen.value = true
    invitationLoading.value = false
    return
  }

  // Store successful invitation info
  invitationInfo.value = {
    inviter: info.inviter!,
    workspaceName: info.workspaceName!,
    userExists: info.userExists!,
  }

  // Show invitation acceptance form for both existing and new users
  pendingInvitationToken.value = props.invitationToken
  isInvitationModalOpen.value = true

  invitationLoading.value = false
}

// Handle invitation modal submission
const handleInvitationModalSubmit = async (userData: {
  password: string | undefined
  profileName: string
  workspaceClientId?: string
}) => {
  // Close modal and clear state
  isInvitationModalOpen.value = false
  invitationError.value = undefined
  invitationLoading.value = false
  pendingInvitationToken.value = undefined

  // Show success message
  showSuccess('Welcome to Routecamp! Your profile has been set up successfully.')

  // Navigate to the workspace
  if (userData.workspaceClientId) {
    router.replace({
      name: 'workspace-detail',
      params: { workspaceId: userData.workspaceClientId },
    })
  } else {
    router.push({ name: 'pinned' })
  }
}

// Handle invitation modal error
const handleInvitationModalError = (errorMessage: string) => {
  invitationError.value = errorMessage
}

// Handle invitation modal close
const handleInvitationModalClose = () => {
  isInvitationModalOpen.value = false
  invitationError.value = undefined
  invitationLoading.value = false
  pendingInvitationToken.value = undefined // Clear token on cancel too
  // Navigate back to home
  router.push({ name: 'pinned' })
}

// UI state
const isDragging = ref(false)
watch(isOfflineMapAreaSelector, (active) => {
  if (active) isDragging.value = false
})
const gpxFileInput = ref<HTMLInputElement | null>(null)
const photoFileInput = ref<HTMLInputElement | null>(null)
const mixedImportFileInput = ref<HTMLInputElement | null>(null)
const trackFilePickerAccept = getTrackFilePickerAccept()
const mixedImportFilePickerAccept = getMixedImportFilePickerAccept()
const mapRef = ref<InstanceType<typeof WorkspaceMapContainer> | null>(null)
const headerRef = ref<InstanceType<typeof MapHeader> | null>(null)
const isLoading = ref(false)
const uploadError = ref<string | null>(null)
const hasInitializedMapRoute = ref(!route.meta.mapOverlay)

// Slideshow state
const isSlideshowOpen = ref(false)
const currentPhotoId = ref<string | undefined>(undefined)

const isFullscreen = ref(false)

const persistedUiState = uiStatePersistenceService.getSnapshot()
if (persistedUiState.slideshow?.isOpen && persistedUiState.slideshow.currentPhotoId) {
  currentPhotoId.value = persistedUiState.slideshow.currentPhotoId ?? undefined
  isSlideshowOpen.value = true
}

const handleFullscreenChange = () => {
  isFullscreen.value = Boolean(document.fullscreenElement)
}
useEventListener(document, 'fullscreenchange', handleFullscreenChange)

const handleFirstSearchVisit = () => {}

const handleSearchTabLeave = () => {}

const handleSearchTabEnter = () => {}

// Use Vue composable for event listeners
useEventListener(window, 'search-tab-first-visit', handleFirstSearchVisit)
useEventListener(window, 'search-tab-leave', handleSearchTabLeave)
useEventListener(window, 'search-tab-enter', handleSearchTabEnter)
useEventListener(window, 'popstate', () => {
  uiStore.beginMapNavigation('back')
})

// Landing page overlay
const handleShowLandingPage = () => {
  void router.push({ name: 'landing-page' })
}
useEventListener(window, 'show-landing-page', handleShowLandingPage)

const cancelLandingPageImportWithError = (message: string) => {
  if (!hasPendingLandingPageImport()) return
  cancelPendingLandingPageImport()
  showError(message)
}

// Both opaque-overlay and transparent-map drops use this one navigation path.
const handleLandingPageImportRequest = () => {
  uiStore.hideLandingPage()
  void router.push({ name: 'pinned' }).then(
    () => {
      if (route.name !== 'pinned') {
        cancelLandingPageImportWithError('Could not open Pinned. No files were imported.')
      }
    },
    (error) => {
      logService.error(LOG_CATEGORIES.GENERAL, 'Failed to open Pinned for landing import', {
        error,
      })
      cancelLandingPageImportWithError('Could not open Pinned. No files were imported.')
    },
  )
}
useEventListener(window, LANDING_PAGE_IMPORT_REQUEST_EVENT, handleLandingPageImportRequest)

// Open slideshow with specific photo
const openSlideshow = (photoId: string) => {
  // Only open in the parent component if not in fullscreen mode
  if (!isFullscreen.value) {
    currentPhotoId.value = photoId
    isSlideshowOpen.value = true
  }
  // If in fullscreen mode, the WorkspaceMapContainer will handle the slideshow
}

const closeSlideshow = () => {
  isSlideshowOpen.value = false
  currentPhotoId.value = undefined
}

const isLocalWorkspaceRoute = (workspaceId: string) =>
  useWorkspaceStore().clientId === workspaceId && useWorkspaceStore()._createEventPushed === false

const maybeOpenWorkspacePhotoRoute = (workspaceId: string) => {
  if (!props.photoId || isSlideshowOpen.value) return

  nextTick(() => {
    const photo = photoStore.photos.find((candidate) => candidate.clientId === props.photoId)
    if (!photo || !props.photoId) return

    openSlideshow(props.photoId)
    router.replace({
      name: 'workspace-detail',
      params: { workspaceId },
    })
  })
}

const applyLoadedWorkspaceRouteState = (
  workspaceId: string,
  options: { moveMap?: boolean; initialMapLoad?: boolean } = {},
) => {
  uiStore.isAllActivitiesView = false
  uiStore.setWorkspacesOverview(false)
  if (options.moveMap !== false) {
    mapRef.value?.zoomToContentWhenReady({
      initialMapLoad: options.initialMapLoad === true,
    })
  }
  maybeOpenWorkspacePhotoRoute(workspaceId)
}

const resetToWorkspacesOverview = async () => {
  try {
    workspaceStore.clearToOverviewState()
    photoStore.$reset()
    uiStore.setWorkspacesOverview(true)
    routeLoadCoordinator.markCurrentStable()
  } catch (error) {
    logService.error(LOG_CATEGORIES.GENERAL, 'Failed to reset stores on home page', { error })
  }
}

const openPinnedSurface = async (options: { preserveMapNavigationIntent?: boolean } = {}) => {
  await nextTick()
  window.dispatchEvent(
    new CustomEvent('open-pinned-surface', {
      detail: { preserveMapNavigationIntent: options.preserveMapNavigationIntent === true },
    }),
  )
}

const openLibrarySurface = async () => {
  workspaceStore.clearToOverviewState()
  photoStore.$reset()
  uiStore.setWorkspacesOverview(false)
  // Opening the Library fits the map to all tours: LibraryTourLayer's mount zoom is gated
  // on auto viewport adaptation, so re-enable it for this navigation context change.
  uiStore.enableAutoViewportAdaptation()
  await nextTick()
  window.dispatchEvent(
    new CustomEvent('open-all-activities', { detail: { preserveMobilePanelState: true } }),
  )
  routeLoadCoordinator.markCurrentStable()
}

const loadWorkspaceRoute = async (
  workspaceId: string,
  options: { forceRemoteLoad?: boolean; initialMapLoad?: boolean } = {},
) => {
  const routeLoadRequest = routeLoadCoordinator.begin()
  if (!options.forceRemoteLoad && isLocalWorkspaceRoute(workspaceId)) {
    applyLoadedWorkspaceRouteState(workspaceId, { initialMapLoad: options.initialMapLoad })
    routeLoadCoordinator.markCurrentStable(routeLoadRequest)
    return true
  }

  isLoading.value = true
  try {
    const loaded = await syncService.loadRemoteWorkspace(workspaceId, false)
    if (!loaded) {
      if (routeLoadCoordinator.isCurrent(routeLoadRequest)) {
        isLoading.value = false
      }
      await routeLoadCoordinator.restoreAfterFailure(routeLoadRequest)
      return false
    }
    if (!routeLoadCoordinator.isCurrent(routeLoadRequest)) return false
    applyLoadedWorkspaceRouteState(workspaceId, { initialMapLoad: options.initialMapLoad })
    routeLoadCoordinator.markCurrentStable(routeLoadRequest)
    return true
  } catch (error) {
    if (routeLoadCoordinator.isCurrent(routeLoadRequest)) {
      uploadError.value = error instanceof Error ? error.message : 'Failed to load remote workspace'
      isLoading.value = false
    }
    await routeLoadCoordinator.restoreAfterFailure(routeLoadRequest)
    return false
  } finally {
    if (routeLoadCoordinator.isCurrent(routeLoadRequest)) {
      isLoading.value = false
    }
  }
}

const loadPinnedRoute = async (options: { initialMapLoad?: boolean } = {}) => {
  const routeLoadRequest = routeLoadCoordinator.begin()
  if (!authStore.isAuthenticated) {
    cancelLandingPageImportWithError('Sign in to import files into Pinned.')
    workspaceStore.clearToOverviewState()
    photoStore.$reset()
    uiStore.setWorkspacesOverview(false)
    await openPinnedSurface({ preserveMapNavigationIntent: options.initialMapLoad })
    routeLoadCoordinator.markCurrentStable(routeLoadRequest)
    return true
  }

  isLoading.value = true
  try {
    const loaded = await syncService.loadPinnedWorkspace()
    if (!loaded) {
      if (routeLoadCoordinator.isCurrent(routeLoadRequest)) {
        cancelLandingPageImportWithError('Pinned could not be loaded. No files were imported.')
        isLoading.value = false
      }
      await routeLoadCoordinator.restoreAfterFailure(routeLoadRequest)
      return false
    }
    if (!routeLoadCoordinator.isCurrent(routeLoadRequest)) return false
    rememberPinnedWorkspaceClientId(workspaceStore.clientId)
    applyLoadedWorkspaceRouteState(workspaceStore.clientId, {
      moveMap: options.initialMapLoad === true,
      initialMapLoad: options.initialMapLoad,
    })
    await openPinnedSurface({ preserveMapNavigationIntent: options.initialMapLoad })
    await processPendingLandingPageImport()
    routeLoadCoordinator.markCurrentStable(routeLoadRequest)
    return true
  } catch (error) {
    if (routeLoadCoordinator.isCurrent(routeLoadRequest)) {
      uploadError.value = error instanceof Error ? error.message : 'Failed to load Pinned'
      cancelLandingPageImportWithError('Pinned could not be loaded. No files were imported.')
      isLoading.value = false
    }
    await routeLoadCoordinator.restoreAfterFailure(routeLoadRequest)
    return false
  } finally {
    if (routeLoadCoordinator.isCurrent(routeLoadRequest)) {
      isLoading.value = false
    }
  }
}

const loadCurrentPersonalSurfaceRoute = async (options: { initialMapLoad?: boolean } = {}) => {
  if (route.name === 'pinned') {
    await loadPinnedRoute(options)
    return true
  }
  if (route.name === 'library') {
    await openLibrarySurface()
    return true
  }
  if (route.name === 'workspaces' && !route.params.workspaceId) {
    await resetToWorkspacesOverview()
    return true
  }
  return false
}

const handleDragEnter = (event: DragEvent) => {
  if (isOfflineMapAreaSelector.value) return
  // Ignore tour reordering drags
  if (event.dataTransfer?.types.includes('application/x-tour-reorder')) {
    return
  }
  isDragging.value = true
}

const handleDragOver = (event: DragEvent) => {
  if (isOfflineMapAreaSelector.value) return
  // Ignore tour reordering drags
  if (event.dataTransfer?.types.includes('application/x-tour-reorder')) {
    return
  }
  isDragging.value = true
}

const handleDragLeave = (event: DragEvent) => {
  if (isOfflineMapAreaSelector.value) return
  // Ignore tour reordering drags
  if (event.dataTransfer?.types.includes('application/x-tour-reorder')) {
    return
  }
  // Check if the mouse is leaving the window
  if (event.relatedTarget === null) {
    isDragging.value = false
  }
}

const handleDrop = (event: DragEvent) => {
  if (isOfflineMapAreaSelector.value) return
  // Ignore tour reordering drags
  if (event.dataTransfer?.types.includes('application/x-tour-reorder')) {
    return
  }
  isDragging.value = false

  const files = event.dataTransfer?.files
  if (files && files.length > 0) {
    // When files are dropped directly on the map (through the transparent map window in the
    // landing page overlay), the overlay never sees the drop event because the map window has
    // pointer-events:none. Dispatch a custom event so the overlay can start its close animation.
    if (uiStore.isLandingPageVisible) {
      if (!queueLandingPageImport(files)) {
        showError('Sign in to import files into Pinned.')
      }
      window.dispatchEvent(new CustomEvent('close-landing-page'))
      return
    }
    void processDroppedFiles(files)
  }
}

// Process dropped files
const processDroppedFiles = async (files: FileList | File[]) => {
  isLoading.value = true
  uploadError.value = null
  const incomingFiles = Array.from(files)

  try {
    // When the All Activities panel is open, drop files straight into the library
    // (no new collection should be created)
    if (uiStore.isAllActivitiesView) {
      const trackFiles = incomingFiles.filter((f) => {
        const name = f.name.toLowerCase()
        return name.endsWith('.gpx') || name.endsWith('.fit')
      })
      if (trackFiles.length > 0) {
        // Match collection upload: always fit the map to the freshly imported
        // tracks. The library zoom-on-upload is gated on auto-viewport adaptation
        // (disabled after any map pan/click), so re-enable it like the native
        // import path does before uploadTourFiles dispatches its zoom event.
        uiStore.enableAutoViewportAdaptation()
        await libraryStore.uploadTourFiles(trackFiles)
        logService.info(LOG_CATEGORIES.GENERAL, 'Dropped files added to library', {
          fileCount: trackFiles.length,
        })
      }
      return
    }

    // Sort files by type
    let trackFiles: File[] = []
    let photoFiles: File[] = []

    for (let i = 0; i < incomingFiles.length; i++) {
      const file = incomingFiles[i]
      const fileName = file.name.toLowerCase()
      if (fileName.endsWith('.gpx') || fileName.endsWith('.fit')) {
        trackFiles.push(file)
      } else if (
        file.type.startsWith('image/') ||
        fileName.endsWith('.heic') ||
        fileName.endsWith('.heif')
      ) {
        photoFiles.push(file)
      }
    }

    // In overview mode (no active workspace), drop track files into the library
    // and open the All Activities panel — never create a new collection on drop.
    if (!workspaceStore.clientId && trackFiles.length > 0) {
      if (!(await requirePermission('upload_gpx'))) return
      // Match collection upload: ensure the map fits the freshly imported tracks.
      uiStore.enableAutoViewportAdaptation()
      await libraryStore.uploadTourFiles(trackFiles)
      logService.info(LOG_CATEGORIES.GENERAL, 'Dropped files added to library from overview', {
        fileCount: trackFiles.length,
      })
      window.dispatchEvent(new CustomEvent('open-all-activities'))
      return
    }

    // Permission checks
    if (trackFiles.length > 0) {
      const allowedTracks = await requirePermission('upload_gpx')
      if (!allowedTracks) {
        trackFiles = []
      }
    }
    if (photoFiles.length > 0) {
      const allowedPhotos = await requirePermission('upload_photos')
      if (!allowedPhotos) {
        photoFiles = []
      }
    }

    // Process track files first if any
    let importedTrackCount = 0
    if (trackFiles.length > 0) {
      const trackImportResult = await workspaceStore.loadTrackFiles(trackFiles)
      importedTrackCount = trackImportResult.importedTourIds.length
      const importedPOICount = trackImportResult.importedPOIIds.length

      if ((importedTrackCount > 0 || importedPOICount > 0) && mapRef.value) {
        nextTick(() => {
          mapRef.value?.zoomToContent()
        })
      }
    }

    // Then process photo files if any
    if (photoFiles.length > 0) {
      await photoStore.loadPhotoFiles(photoFiles)

      // Only zoom to all content again if there were no track files
      // (if tracks were imported, we've already zoomed to them)
      if (importedTrackCount === 0 && mapRef.value) {
        nextTick(() => {
          mapRef.value?.zoomToContent()
        })
      }
    }
  } catch (error) {
    uploadError.value = error instanceof Error ? error.message : 'Unknown error occurred'
  } finally {
    isLoading.value = false
  }
}

async function processPendingLandingPageImport() {
  if (!hasPendingLandingPageImport()) return

  try {
    const files = consumePendingLandingPageImport({
      isPinnedRoute: route.name === 'pinned',
      workspaceClientId: workspaceStore.clientId,
      systemKey: workspaceStore.systemKey ?? null,
    })
    if (files.length > 0) await processDroppedFiles(files)
  } catch (error) {
    logService.error(LOG_CATEGORIES.GENERAL, 'Cancelled landing page import', { error })
    showError(`${error instanceof Error ? error.message : String(error)}. No files were imported.`)
  }
}

const handleGpxInputChange = async (event: Event) => {
  const input = event.target as HTMLInputElement
  if (!input.files || input.files.length === 0) return

  const trackFiles = Array.from(input.files).filter((f) => /\.(gpx|fit)$/i.test(f.name))
  const imageFiles = Array.from(input.files).filter((f) => {
    const name = f.name.toLowerCase()
    return (
      name.endsWith('.jpg') ||
      name.endsWith('.jpeg') ||
      name.endsWith('.png') ||
      name.endsWith('.webp') ||
      name.endsWith('.heic') ||
      name.endsWith('.heif')
    )
  })

  if (uiStore.isAllActivitiesView) {
    if (trackFiles.length > 0) {
      if (!(await requirePermission('upload_gpx'))) {
        input.value = ''
        return
      }
      // Match collection upload: re-enable auto-viewport adaptation so the
      // library zoom-on-upload fits the freshly imported tracks (see drop path).
      uiStore.enableAutoViewportAdaptation()
      await libraryStore.uploadTourFiles(trackFiles)
    }
    if (imageFiles.length > 0) {
      showInfo('Photos can only be imported into a collection.')
    }
    input.value = ''
    return
  }

  if (trackFiles.length > 0) {
    if (!(await requirePermission('upload_gpx'))) {
      input.value = ''
      return
    }
    if (!workspaceStore.clientId) {
      workspaceStore.$reset()
      logService.info(LOG_CATEGORIES.GENERAL, 'Created new workspace for GPX upload', {
        fileCount: trackFiles.length,
      })
    }
    const dt = new DataTransfer()
    trackFiles.forEach((f) => dt.items.add(f))
    const trackImportResult = await workspaceStore.loadTrackFiles(dt.files)
    if (
      trackImportResult.importedTourIds.length > 0 ||
      trackImportResult.importedPOIIds.length > 0
    ) {
      nextTick(() => mapRef.value?.zoomToContent())
    }
  }

  if (imageFiles.length > 0) {
    if (!(await requirePermission('upload_photos'))) {
      input.value = ''
      return
    }
    if (!workspaceStore.clientId) {
      workspaceStore.$reset()
      logService.info(LOG_CATEGORIES.GENERAL, 'Created new workspace for photo upload', {
        fileCount: imageFiles.length,
      })
    }
    const dt = new DataTransfer()
    imageFiles.forEach((f) => dt.items.add(f))
    await photoStore.loadPhotoFiles(dt.files)
    nextTick(() => mapRef.value?.zoomToContent())
  }

  input.value = ''
}

const handlePhotoInputChange = async (event: Event) => {
  const input = event.target as HTMLInputElement
  if (input.files && input.files.length > 0) {
    if (!(await requirePermission('upload_photos'))) {
      input.value = ''
      return
    }
    // Check if we have an active workspace, if not create a new one
    if (!workspaceStore.clientId) {
      // Initialize a new workspace when in collections overview mode
      workspaceStore.$reset()
      logService.info(LOG_CATEGORIES.GENERAL, 'Created new workspace for photo upload', {
        fileCount: input.files.length,
      })
    }

    await photoStore.loadPhotoFiles(input.files)

    input.value = ''

    nextTick(() => {
      mapRef.value?.zoomToContent()
    })
  }
}

const handleGpxUploadTrigger = async () => {
  if (!(await requirePermission('upload_gpx'))) return
  gpxFileInput.value?.click()
}

const handlePhotoUploadTrigger = async () => {
  if (!(await requirePermission('upload_photos'))) return
  photoFileInput.value?.click()
}

const handleMixedUploadTrigger = () => {
  mixedImportFileInput.value?.click()
}

const handleNavigateToLocation = (photoId: string) => {
  // First close the slideshow
  closeSlideshow()

  // Then zoom to the photo location on the map
  nextTick(() => {
    mapRef.value?.zoomToPhoto(photoId)
  })
}

onMounted(async () => {
  // Handle invitation acceptance first if we have a token
  if (props.invitationToken) {
    await handleInvitationAcceptance()
    return // Exit early after handling invitation
  }

  // Auxiliary pages are rendered above a cached map and must not change its state.
  if (route.meta.mapOverlay) {
    return
  }

  if (await loadCurrentPersonalSurfaceRoute({ initialMapLoad: true })) {
    hasInitializedMapRoute.value = true
    return
  }

  const workspaceId = route.params.workspaceId
  if (workspaceId && typeof workspaceId === 'string') {
    await loadWorkspaceRoute(workspaceId, { initialMapLoad: true })
    hasInitializedMapRoute.value = true
  }
})

watch(
  () => props.invitationToken,
  async (invitationToken, previousInvitationToken) => {
    if (!invitationToken || invitationToken === previousInvitationToken) return
    await handleInvitationAcceptance()
  },
)

// Watch for route changes to handle personal surfaces and workspace loading
watch(
  () => [route.path, Boolean(route.meta.mapOverlay)] as const,
  async ([, isMapOverlay], [oldPath, wasMapOverlay]) => {
    if (isMapOverlay) {
      return
    }

    if (wasMapOverlay && hasInitializedMapRoute.value) {
      uiStore.setRestoringFromStaticPage(true)
      await nextTick()
      uiStore.setRestoringFromStaticPage(false)
      return
    }

    // Skip expensive operations if we're just restoring from a static page
    if (uiStore.isRestoringFromStaticPage) {
      return
    }

    if (route.name === 'pinned' || route.name === 'library') {
      await loadCurrentPersonalSurfaceRoute({
        initialMapLoad: !hasInitializedMapRoute.value,
      })
      hasInitializedMapRoute.value = true
      return
    }

    // If navigating to collections overview, clear to workspaces overview state.
    if (route.name === 'workspaces' && !route.params.workspaceId) {
      const cameFromWorkspaceRoute =
        typeof oldPath === 'string' &&
        (oldPath.startsWith('/collections/') || oldPath.startsWith('/workspaces/'))
      if (cameFromWorkspaceRoute) {
        uiStore.beginMapNavigation('back')
      }
      await resetToWorkspacesOverview()
      hasInitializedMapRoute.value = true
      return
    }
  },
)

watch(
  [
    () => uiStore.isSidebarVisible,
    () => uiStore.isLayersPanelVisible,
    () => uiStore.activePanel,
    () => uiStore.isMobileBottomPanelExpanded,
    () => uiStore.isInWorkspacesOverview,
    () => uiStore.autoViewportAdaptationEnabled,
  ],
  ([
    isSidebarVisible,
    isLayersPanelVisible,
    activePanel,
    isMobileBottomPanelExpanded,
    isInWorkspacesOverview,
    autoViewportAdaptationEnabled,
  ]) => {
    uiStatePersistenceService.updateUiState({
      isSidebarVisible,
      isLayersPanelVisible,
      activePanel,
      isMobileBottomPanelExpanded,
      isInWorkspacesOverview,
      autoViewportAdaptationEnabled,
    })
  },
  { immediate: true },
)

watch(
  [isSlideshowOpen, currentPhotoId],
  ([isOpen, photoId]) => {
    uiStatePersistenceService.updateSlideshowState({
      isOpen,
      currentPhotoId: isOpen ? (photoId ?? null) : null,
    })
  },
  { immediate: true },
)

// Watch for route changes to load different workspaces
watch(
  () => route.params.workspaceId,
  async (newWorkspaceId) => {
    // Auxiliary pages should not trigger workspace loading while the map is cached behind them.
    if (
      route.name === 'pinned' ||
      route.name === 'library' ||
      (route.name === 'workspaces' && !route.params.workspaceId) ||
      route.meta.mapOverlay
    ) {
      return
    }

    // Skip expensive operations if we're just restoring from a static page
    if (uiStore.isRestoringFromStaticPage) {
      return
    }

    if (newWorkspaceId && typeof newWorkspaceId === 'string') {
      await loadWorkspaceRoute(newWorkspaceId, {
        initialMapLoad: !hasInitializedMapRoute.value,
      })
      hasInitializedMapRoute.value = true
    }
  },
)

watch(
  () => authStore.isAuthenticated,
  async (isAuthenticated, wasAuthenticated) => {
    if (!isAuthenticated || wasAuthenticated) return
    if (route.meta.mapOverlay) return

    if (
      route.name === 'pinned' ||
      route.name === 'library' ||
      (route.name === 'workspaces' && !route.params.workspaceId)
    ) {
      await loadCurrentPersonalSurfaceRoute({ initialMapLoad: route.name === 'pinned' })
      return
    }
    const workspaceId = route.params.workspaceId
    if (!workspaceId || typeof workspaceId !== 'string') return
    await loadWorkspaceRoute(workspaceId, { forceRemoteLoad: true })
  },
)
</script>

<template>
  <div
    data-testid="workspace-drop-zone"
    class="w-full h-full relative overflow-hidden"
    @dragenter.prevent="handleDragEnter"
    @dragover.prevent="handleDragOver"
    @dragleave.prevent="handleDragLeave"
    @drop.prevent="handleDrop"
  >
    <MapHeader v-if="route.name !== 'offline-map-areas'" ref="headerRef" />

    <!-- Fullscreen container - wraps both map and toolbar -->
    <div id="map-fullscreen-container" class="w-full h-full relative">
      <!-- Map component with waypoint hover events -->
      <WorkspaceMapContainer
        ref="mapRef"
        :header-ref="headerRef"
        :render-header="false"
        @view-photo="openSlideshow"
        @trigger-gpx-upload="handleGpxUploadTrigger"
        @trigger-photo-upload="handlePhotoUploadTrigger"
        @trigger-mixed-upload="handleMixedUploadTrigger"
      />

      <!-- Drag & drop overlay - Moved inside fullscreen container -->
      <div
        v-if="isDragging && !uiStore.isLandingPageVisible && !isOfflineMapAreaSelector"
        :class="[
          // Positioning
          'absolute',
          'inset-0',
          // Layout
          'flex',
          'items-center',
          'justify-center',
          // Styling
          'bg-primary-100',
          'z-40',
          'pointer-events-none',
        ]"
      >
        <div class="bg-white p-8 rounded-lg shadow-xl border-2 border-primary-500">
          <p class="text-2xl font-bold text-primary-700">Drop Files</p>
          <p class="text-gray-600 mt-2">GPX/FIT tracks and photos supported</p>
        </div>
      </div>
    </div>

    <input
      ref="gpxFileInput"
      type="file"
      :accept="trackFilePickerAccept"
      @change="handleGpxInputChange"
      class="hidden"
      data-testid="workspace-gpx-input"
      multiple
    />
    <input
      ref="mixedImportFileInput"
      type="file"
      :accept="mixedImportFilePickerAccept"
      @change="handleGpxInputChange"
      class="hidden"
      multiple
    />
    <input
      ref="photoFileInput"
      type="file"
      accept="image/*"
      @change="handlePhotoInputChange"
      class="hidden"
      multiple
    />

    <!-- Elements that should remain outside the fullscreen container -->

    <!-- Photo slideshow -->
    <PhotoSlideshow
      :is-open="isSlideshowOpen"
      :initial-photo-id="currentPhotoId"
      @close="closeSlideshow"
      @navigate-to-location="handleNavigateToLocation"
    />

    <!-- Loading error overlay -->
    <div
      v-if="uploadError"
      :class="[
        // Positioning
        'absolute',
        'inset-0',
        // Layout
        'flex',
        'items-center',
        'justify-center',
        // Styling
        'backdrop-blur-sm',
        'bg-gray-800/70',
        'z-40',
      ]"
    >
      <div class="bg-white p-8 rounded-lg shadow-xl">
        <div class="flex flex-col items-center">
          <div
            class="w-12 h-12 border-4 border-t-4 border-primary-500 rounded-full animate-spin"
          ></div>
          <p class="mt-2 text-red-500">{{ uploadError }}</p>
        </div>
      </div>
    </div>
  </div>

  <!-- Invitation Acceptance Modal -->
  <AuthModal
    :is-open="isInvitationModalOpen"
    initial-view="invitation-acceptance"
    :invitation-token="pendingInvitationToken"
    :invitation-error="invitationError"
    :invitation-loading="invitationLoading"
    :invitation-info="invitationInfo"
    @invitation-success="handleInvitationModalSubmit"
    @invitation-error="handleInvitationModalError"
    @close="handleInvitationModalClose"
  />
</template>
